import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import BDButton from '@shared/components/elements/button';
import BDTitle from '@shared/components/elements/title';
import { HSection } from '@shared/components/home/section';
import {
  CLUB_INFOS,
  DIAMOND_DUE_RATIO,
  GOLD_CLUB_INFO,
  SECOND_DIAMOND_DISCOUNT_RATIO,
} from '@shared/lib/constants';
import { capitalize } from '@shared/lib/utils';
import { getCarts, groupLinesInCart } from '@shared/lib/utils/cart-utils';
import { rPercentage } from '@shared/lib/utils/formats';
import { renderMoney } from '@shared/lib/utils/money';
import { getBaseDiamondProduct } from '@shared/lib/utils/product-server-utils';
import Link from 'next/link';

type FAQType = {
  question: string;
  answer: React.ReactNode;
};

export const FAQListItem = ({ faq }: { faq: FAQType }) => {
  return (
    <Disclosure>
      <DisclosureButton className="group flex flex-row items-start justify-start gap-2 py-2">
        <ChevronDownIcon className="group-data-open:border-bd-primary group-data-open:bg-bd-primary group-data-open:text-bd-light border-bd-dark group-data-open:rotate-180 w-8 flex-shrink-0 rounded-full border transition duration-300" />
        <p className="group-data-open:text-bd-primary text-left text-lg transition-transform duration-300">
          {faq.question}
        </p>
      </DisclosureButton>
      <DisclosurePanel className="flex flex-col gap-2 px-10">{faq.answer}</DisclosurePanel>
    </Disclosure>
  );
};

export const orderProcessFaqs = () =>
  [
    {
      question: 'How long does it take to create a Bonded Diamond™?',
      answer: (
        <p>
          It takes approximately <span className="underline">3 months</span> to receive your Bonded
          Diamond™. If you have a special occasion coming up, we recommend you contact us and
          choose expedited premium processing, which can guarantee a delivery date within{' '}
          <span className="underline">2 months</span> with an additional fee.
        </p>
      ),
    },
    {
      question: 'How does the order flow work?',
      answer: (
        <>
          <p>The order flow is as follows:</p>
          <ol className="list-decimal pl-8 text-sm">
            <li>
              <span className="font-normal">Purchase a get started kit</span> to kick off the
              diamond building process. You are not obligated to purchase a Bonded Diamond™ or
              jewelry from us at this time.
            </li>
            <li>
              <span className="font-normal">Collect joint hair and return</span> - The kit contains
              instructions and tools for you to collect a small amount of hair (6 grams for every
              carat weight of diamond you plan to order). A return shipping label is included to
              return the collected hair back to our facility.
            </li>
            <li>
              <span className="font-normal">Finalize your diamond and jewelry order</span> - The kit
              also contains sample cubic zirconia and ring sizer to help you finalize your diamond
              and jewelry pick. The get started kit fee will be deducted at the time of your order.
            </li>
            <li>
              <span className="font-normal">We keep you updated</span> - We will immediately kick
              start the diamond growth process. We will keep you updated on the progress of your
              diamond growth, polishing, cutting, engraving, and certification.
            </li>
            <li>
              <span className="font-normal">Finalize and Delivery</span> - Once the diamond and its
              jewelry are ready, we will send you a notification and ask you to make the final
              payment. We will then express ship your Bonded Diamond™ to you.
            </li>
          </ol>
          <p>
            Read more about the order process{' '}
            <Link href="/learn-more/#order-process" className="underline">
              here
            </Link>
            .
          </p>
        </>
      ),
    },
    {
      question: 'Do you allow returns?',
      answer: (
        <>
          <p>
            We stand behind the quality of your Bonded Diamond™ and your 100% satisfaction. Because
            all of our jewelry is made to order, it is generally non-refundable.
          </p>
          <p>
            However, our Get Started Kit is always auto-refunded at the time of any Bonded Diamond™
            order. If you decide not to order a Bonded Diamond™, the get started kit is refundable
            within <span className="underline">90 days</span> when returned in new and unused
            condition.
          </p>
          <p>
            A Bonded Diamond™ is refundable within <span className="underline">30 days</span> if it
            does not meet the guaranteed 4C specifications or a promised delivery date, in which
            case we will issue a full refund after the diamond is returned for destruction.
          </p>
          <p>
            Jewelry is refundable in <span className="underline">30 days</span>if the item does not
            match the product description stated.
          </p>
        </>
      ),
    },
    {
      question: 'When do I need to pay for my Bonded Diamond™?',
      answer: (
        <>
          <p>
            There is no commitment to purchase a Bonded Diamond™ or jewelry from us to order a get
            started kit. You will be asked to make payment for the Bonded Diamond™ to confirm your
            order, usually at the time of you returning your collected hair.
          </p>
          {paymentFaq}
        </>
      ),
    },
  ] as FAQType[];

const discountFaq = (
  <>
    <ul className="flex list-disc flex-col gap-2 pl-8 text-sm">
      {CLUB_INFOS.map((clubInfo) => (
        <li key={clubInfo.tier}>
          {capitalize(clubInfo.tier)} members enjoy a {rPercentage(clubInfo.discount)} discount on
          all Bonded Diamonds™ and jewelry.
        </li>
      ))}
    </ul>
    <p>
      Checkout how to join the club for free{' '}
      <Link href="/club" className="underline">
        here
      </Link>
      .
    </p>
    <p>
      Military personnel and first responders always receive a{' '}
      {rPercentage(GOLD_CLUB_INFO.discount)} discount on all Bonded Diamonds™ and jewelry, please
      contact us if you are eligible.
    </p>
  </>
);

const paymentFaq = (
  <>
    <p>Currently, there are 2 types of payment plans available:</p>
    <ul className="flex list-disc flex-col gap-2 pl-8 text-sm">
      <li>
        <p>
          <span className="font-normal">50% upfront</span> - You will pay{' '}
          {rPercentage(DIAMOND_DUE_RATIO)} + tax at order confirmation, and the remaining will be
          collected before the time of order delivery.
        </p>
      </li>
      <li>
        <p>
          <span className="font-normal">No Credit Check 4 Easy Installments</span> - If you elect to
          our no credit check 4 easy payment plan. You will pay 25% + tax at order confirmation, and
          the rest will be paid in 3 monthly installments. Terms and conditions apply.
        </p>
      </li>
    </ul>
  </>
);

export const getStartedKitFaqs = async (): Promise<FAQType[]> => {
  return [
    {
      question: 'Why should I purchase a get started kit?',
      answer: (
        <>
          <p>
            The get started kit is a collection of items that will help you collect your joint hair
            and finalize your diamond and jewelry selection.
          </p>
          <p>
            It contains a hair collection kit with instructions and tools for you to collect a small
            amount of hair (6 grams for every carat weight of diamond you plan to order). A return
            shipping label is included to return the collected hair back to our facility.
          </p>
          <p>
            It also contains a cubic zirconia stone and a ring sizer to help you finalize your
            diamond and jewelry selection.
          </p>
        </>
      ),
    },
    {
      question: 'Is the get started kit refundable?',
      answer: (
        <>
          <p>
            The get started kit will always be automatically refunded at the time of any Bonded
            Diamond™ order.
          </p>
          <p>
            If you do not want to proceed with the Bonded Diamond™ order, you can{' '}
            <Link href="/contact" className="underline">
              contact us
            </Link>{' '}
            to return your get started kit for a refund within{' '}
            <span className="underline">90 days.</span>
          </p>
        </>
      ),
    },
    {
      question: 'Is a get started kit required?',
      answer: (
        <>
          <p>
            While most customers find it convenient to purchase a get started kit, and get it
            refunded when they confirm their Bonded Diamond™ order, the kit itself is not required
            to start the diamond building process.
          </p>
          <p>
            If you have a short time frame to get your Bonded Diamond™ ready,{' '}
            <Link href="/contact" className="underline">
              contact us
            </Link>{' '}
            to see if you can skip the get started kit and start the diamond building process
            immediately.
          </p>
        </>
      ),
    },
    {
      question: 'Am I obligated to purchase a Bonded Diamond™ after purchasing a get started kit?',
      answer: (
        <p>
          No, there is no obligation to purchase a Bonded Diamond™ after purchasing a get started
          kit. You will only be charged for the get started kit today to cover the cost of the kit
          items only.
        </p>
      ),
    },
  ];
};

export const diamondFaqs = async (): Promise<FAQType[]> => {
  const diamondProduct = await getBaseDiamondProduct();
  const diamondStartingAt = diamondProduct.priceRange.minVariantPrice;
  const { cart, isFirstTimeOrder } = await getCarts();
  const { kit } = groupLinesInCart(cart);
  return [
    {
      question: 'How much does a Bonded Diamond™ cost?',
      answer: (
        <p>
          We offer transparent pricing. The most affordable Bonded Diamond™ is a 1/4 carat round
          diamond, which costs {renderMoney(diamondStartingAt)}. This price covers the entire
          process, including the hair collection kit, diamond creation, cut polishing,
          certification, and shipping. You may easily find our diamond pricing{' '}
          <Link href="/shop/diamonds/bonded" className="underline">
            here
          </Link>
          , and our jewelry pricing{' '}
          <Link href="/shop/jewelry" className="underline">
            here
          </Link>
          .
        </p>
      ),
    },
    {
      question: 'Why are some Bonded Diamond™ shapes more expensive than others?',
      answer: (
        <p>
          For shapes other than round, we need to grow a larger raw diamond to achieve the desired
          cut. Additionally, these special shapes require more precision and expertise to cut and
          polish, making the process more time-intensive and resource-demanding.
        </p>
      ),
    },
    {
      question: `Why do multiple Bonded Diamonds™ come with a ${rPercentage(
        SECOND_DIAMOND_DISCOUNT_RATIO
      )} discount?`,
      answer: (
        <>
          <p>
            Bonded Diamonds™ are made to order, meaning each order requires a separate diamond
            growth process. However, when you purchase multiple Bonded Diamonds™ in a single order,
            we can process the collected hair in one batch and grow multiple diamonds in a single
            growth cycle. This batching process reduces costs, allowing us to pass those savings to
            you.
          </p>
          <p>
            If your order includes Bonded Diamonds™ of different sizes and shapes, the{' '}
            {rPercentage(SECOND_DIAMOND_DISCOUNT_RATIO)} discount will be applied to the lower-value
            diamond. This is because larger raw diamonds are required to achieve certain sizes and
            shapes, limiting the savings we can achieve.
          </p>
        </>
      ),
    },
    {
      question: 'Do you offer any discounts?',
      answer: (
        <>
          <p>
            We strive to provide the best and transparent pricing, and the only discounts we offer
            are through our membership program, the{' '}
            <Link href="/club#join-club" className="underline">
              Bonded Diamond™ Club
            </Link>
            .
          </p>
          {discountFaq}
        </>
      ),
    },
    {
      question: 'What size of Bonded Diamond™ shall I get?',
      answer: (
        <>
          <p>We offer Bonded Diamond™s in a range of sizes, from 1/4 carat to 3 carats.</p>
          <p>
            You will find sample cubic zirconia stones in our get started kit. You can use these
            stones to help you decide on the size of your Bonded Diamond™.
          </p>
          <p>
            Since each Bonded Diamond™ is made to order, we carefully control the growth process to
            ensure that your diamond is at least the size you selected.
          </p>
        </>
      ),
    },
    {
      question: 'What makes my Bonded Diamond™ unique?',
      answer: (
        <>
          <p>
            Each Bonded Diamond™ is unique because they are made from carbon source from your hair.
            Therefore, building a Bonded Diamond™ with us is quite different from purchasing a
            diamond from a jeweler. Every Bonded Diamond™ is uniquely connected to their creator,
            symbolizing the enduring bonds between those who contribute to its creation.
          </p>
          <p>
            We offer you a free engraving service for your Bonded Diamond™. You can engrave a
            special message of your choice, such as a name, date, or a special message on the girdle
            of your Bonded Diamond™.
          </p>
        </>
      ),
    },
    {
      question: 'Do Bonded Diamond™s have certifications?',
      answer: (
        <p>
          Every Bonded Diamond™ comes with an IGI (International Gemological Institute)
          certification, verifying the quality and authenticity of the diamond. Additionally, you
          will receive a signed certificate from us that provides information about the carbon
          source and the integrity of our process.
        </p>
      ),
    },
    {
      question: 'How to order a Bonded Diamond™?',
      answer: (
        <>
          {isFirstTimeOrder ? (
            kit?.length ? (
              <p>
                Complete purchase of the get started kit today. You will be invited to confirm your
                Bonded Diamond™ order when you are ready to return your collected hair.
              </p>
            ) : (
              <p>
                Because all the Bonded Diamond™s are made to order from your joint hair strands,
                you may start the process by ordering a get started kit{' '}
                <Link href="/shop/service" className="underline">
                  here
                </Link>
                .
              </p>
            )
          ) : (
            <p>
              You may confirm your Bonded Diamond™ selection{' '}
              <Link href="/shop/diamonds/bonded" className="underline">
                here
              </Link>
              . You will be asked to make payment for the Bonded Diamond™ to confirm your order.
            </p>
          )}
        </>
      ),
    },
    {
      question: 'What is the payment process for a Bonded Diamond™ order?',
      answer: (
        <>
          <p>
            You will be asked to make payment for the Bonded Diamond™ to confirm your order. At the
            confirmation page, you will be asked to verify your order details and select a payment
            plan.
          </p>
          {paymentFaq}
        </>
      ),
    },
  ];
};

export const jewelryFaqs = async (): Promise<FAQType[]> => {
  const { isFirstTimeOrder } = await getCarts();
  return [
    {
      question: 'Why should I choose Bonded Diamond™ jewelry?',
      answer: (
        <>
          <p>
            Bonded Diamond™ jewelry is designed to perfectly complement your Bonded Diamond™, with
            a selection of rings, necklaces, and earrings that enhance its brilliance.
          </p>
          <p>
            We take pride in being an American company, crafting all our jewelry in the USA to
            ensure the highest quality and craftsmanship.
          </p>
          <p>
            If you have a specific design in mind, we're happy to explore custom options based on
            your inspiration photos or drawings. Additionally, you're not limited to purchasing
            jewelry from us—we're more than happy to collaborate with your preferred jeweler to
            create the perfect setting for your Bonded Diamond™.
          </p>
        </>
      ),
    },
    {
      question: 'Do you offer any discounts on jewelry?',
      answer: (
        <>
          <p>
            We strive to provide the best and transparent pricing, and the only discounts we offer
            are through our membership program, the
            <Link href="/club#join-club" className="underline">
              Bonded Diamond™ Club
            </Link>
            .
          </p>
          {discountFaq}
        </>
      ),
    },
    {
      question: 'How to customize my jewelry?',
      answer: (
        <>
          <p>
            Every Bonded Diamond™ jewelry is designed to perfectly complement your Bonded
            Diamond™.
          </p>
          <p>
            If you have made selection of a Bonded Diamond™, you will find a quick filter toggle to
            show only the jewelry that is compatible with your Bonded Diamond™ selection.
          </p>
          <p>
            If you do not have a Bonded Diamond™ selected yet, you can browse our jewelry
            selections{' '}
            <Link href="/shop/jewelry" className="underline">
              here
            </Link>
            . Every Bonded Diamond™ jewelry customization page will allow you to select a Bonded
            Diamond™ that can fit to the specific jewelry piece.
          </p>
        </>
      ),
    },
    {
      question: 'What is the order process for jewelry?',
      answer: (
        <>
          {isFirstTimeOrder ? (
            <p>
              Feel free to browse our jewelry selections{' '}
              <Link href="/shop/jewelry" className="underline">
                here
              </Link>
              . Your jewelry selection will be saved in your account for future orders.
            </p>
          ) : (
            <p>
              You may confirm your jewelry selection{' '}
              <Link href="/shop/jewelry" className="underline">
                here
              </Link>
              . You will be asked to make payment for the jewelry to confirm your order.
            </p>
          )}
        </>
      ),
    },
    {
      question: 'What is the payment process for jewelry?',
      answer: (
        <>
          <p>
            You will be asked to make payment for the jewelry to confirm your order. At the
            confirmation page, you will be asked to verify your order details and select a payment
            plan.
          </p>
          {paymentFaq}
        </>
      ),
    },
  ];
};

export const clubFaqs = async (): Promise<FAQType[]> => [
  {
    question: 'What is the Bonded Diamond™ Club?',
    answer: (
      <>
        <p>
          The Bonded Diamond™ Club is our loyalty program that offers a range of benefits and perks
          to our beloved customers.
        </p>
        <p>
          The Bonded Diamond™ Club is free to join. It has 3 tiers: Silver, Gold, and Platinum,
          each with different benefits and perks.
        </p>
        <p>
          The membership tiers are similar to airline programs tiers, and not subscription services
          like Netflix. There is no monthly or annual membership fee nor will we automatically
          charge you.
        </p>
      </>
    ),
  },
  {
    question: 'How to join the Bonded Diamond™ Club?',
    answer: (
      <p>
        You can enroll for the membership and immediately start to enjoy the benefits and perks by
        visiting{' '}
        <Link href="/club#join-club" className="underline">
          our membership page
        </Link>
        .
      </p>
    ),
  },
  {
    question: 'How to opt out of occasional marketing emails?',
    answer: (
      <p>
        You can opt out of occasional marketing emails by visiting{' '}
        <Link href="/customer/membership" className="underline">
          your membership page
        </Link>
        . Or type in your email address in the{' '}
        <Link href="/customer/unsubscribe" className="underline">
          unsubscribe section
        </Link>
        . You may keep your membership but opt out of our occasional marketing emails.
      </p>
    ),
  },
  {
    question: 'How to opt out of the Bonded Diamond™ Club?',
    answer: (
      <p>
        You can opt out of the Bonded Diamond™ Club by visiting{' '}
        <Link href="/customer/membership" className="underline">
          your membership page
        </Link>
        . Or contact us{' '}
        <Link href="/contact" className="underline">
          here
        </Link>
        .
      </p>
    ),
  },
  {
    question: 'How to qualify for the Gold and Platinum membership?',
    answer: (
      <>
        <p>
          You can qualify for the Gold and Platinum membership tiers by making qualifying purchases
          of Bonded Diamond™ and jewelry or by paying for the Gold and Platinum membership tiers.
        </p>
        <p>
          Please visit the Bonded Diamond™ Club{' '}
          <Link href="/club#how-to-qualify" className="underline">
            membership page
          </Link>{' '}
          for more information.
        </p>
        <p>
          Note that although it is possible to qualify for the Gold and Platinum membership tiers by
          making a one time payment, the membership is not automatically renewed. We will never
          automatically charge your card.
        </p>
      </>
    ),
  },
  {
    question:
      'My Gold/Platinum membership tier is about to expire, will my order still enjoy the discount?',
    answer: (
      <p>
        Yes, your order will still enjoy the discount if the order is placed before your membership
        tier expires.
      </p>
    ),
  },
  {
    question: 'I have made a qualifying purchase, why am I not seeing the discount?',
    answer: (
      <>
        <p>
          Your in-progress qualifying purchase will not upgrade your membership tier until the
          purchase is fulfilled. Generally speaking, the first Bonded Diamond™ order will upgrade a
          customer's membership tier after that order is completed, making their future purchases
          eligible for a membership discount.
        </p>
        <p>
          It will take up to 1 week for us to update your membership status after your qualifying
          purchase is completed. Please <Link href="/contact">contact us</Link> if you have any
          questions.
        </p>
      </>
    ),
  },
];

export const generalFaqs = async (): Promise<FAQType[]> => {
  const diamondFaqsList = await diamondFaqs();
  const orderFlowFaqsList = orderProcessFaqs();
  return [
    {
      question: 'What makes Bonded Diamond™s unique?',
      answer: (
        <p>
          Each Bonded Diamond™ is unique because they are made from carbon source from your hair.
          Therefore, building a Bonded Diamond™ with us is quite different from purchasing a
          diamond from a jeweler. Every Bonded Diamond™ is uniquely connected to their creator,
          symbolizing the enduring bonds between those who contribute to its creation.
        </p>
      ),
    },
    diamondFaqsList[0]!,
    orderFlowFaqsList[0]!,
    diamondFaqsList[6]!,
    diamondFaqsList[3]!,
    orderFlowFaqsList[1]!,
    diamondFaqsList[8]!,
    {
      question: 'How do I get started?',
      answer: (
        <p>
          You may learn more about our process and how to get started by purchasing a get started
          kit{' '}
          <Link href="/shop/service" className="underline">
            here
          </Link>
          . Should you have any questions, please{' '}
          <Link href="/contact" className="underline">
            contact us
          </Link>
          .
        </p>
      ),
    },

    // {
    //   question: 'How does the custom design process work?',
    //   answer: (
    //     <>
    //       <p>
    //         We can build almost any type of jewelry through our custom design service. After you kick
    //         off the diamond building process, contact us to be matched with a dedicated designer who
    //         will work with you for any inspiration photos or drawings you have. The designer will then
    //         collaborate with our team to create a 3D model of the jewelry for your approval. You are
    //         not obligated to purchase the jewelry if you are not satisfied with the design.
    //       </p>
    //       <p>
    //         We do not limit you to purchase a jewelry from us. We are more than happy to work with any
    //         jeweler you want and help you to build the perfect center Bonded Diamond™ for their
    //         specifications.
    //       </p>
    //     </>
    //   )
    // }
  ];
};

export default async function FAQ() {
  return (
    <HSection>
      <BDTitle>Frequently Asked Questions</BDTitle>

      <div className="flex flex-col gap-4">
        {(await generalFaqs()).map((faq, index) => (
          <FAQListItem key={index} faq={faq} />
        ))}
      </div>

      <div className="mt-8 flex flex-col items-start gap-8">
        <BDButton
          href="/learn-more#order-process"
          styleType="base"
          className="text-bd-primary border-none text-xl font-light lg:text-2xl"
        >
          Order Process FAQs {'>'}
        </BDButton>

        <BDButton
          href="/shop/diamonds#faqs"
          styleType="base"
          className="text-bd-primary border-none text-xl font-light lg:text-2xl"
        >
          Bonded Diamond™ FAQs {'>'}
        </BDButton>

        <BDButton
          href="/shop/jewelry#faqs"
          styleType="base"
          className="text-bd-primary border-none text-xl font-light lg:text-2xl"
        >
          Bonded Diamond™ Jewelry FAQs {'>'}
        </BDButton>

        <BDButton
          href="/club"
          styleType="base"
          className="text-bd-primary border-none text-xl font-light lg:text-2xl"
        >
          Bonded Diamond™ Club FAQs {'>'}
        </BDButton>
      </div>
    </HSection>
  );
}
